import { FC, useState, useEffect } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, InputAdornment } from '@mui/material';
import { SCUIButton, SCUIDateRanger } from '../../../../../atomic/atoms';
import { SCUIGeneralDrawerHeader } from '../../../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { initialState } from '../../ReturnList.helper';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TagIcon from '@mui/icons-material/Tag';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterListIcon from '@mui/icons-material/FilterList';

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        fontSize: 13,
        transition: 'all 0.2s ease',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: 13,
        fontWeight: 500,
    },
    '& .MuiInputAdornment-root svg': {
        fontSize: 18,
        color: 'primary.main',
    },
};

const selectSx = {
    borderRadius: 2,
    fontSize: 13,
    transition: 'all 0.2s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: 2,
    },
};

export const SCUICreateReturnFilterDrawerContent: FC<{
    onClose?: any;
    setReturnDetailFilterInput: any;
    returnStatus: any;
}> = ({ onClose, setReturnDetailFilterInput, returnStatus }) => {
    const [returnDetailFilterInputForDrawer, setReturnDetailFilterInputForDrawer] = useState(
        initialState.returnDetailFilterInput
    );

    const { t } = useTranslation();

    useEffect(() => {
        setReturnDetailFilterInput(initialState.returnDetailFilterInput);
    }, []);

    const _handlChangeDate = (event: any) => {
        setReturnDetailFilterInputForDrawer({
            ...returnDetailFilterInputForDrawer,
            StartDate: event[0],
            EndDate: event[1],
        });
    };
    const clearForm = () => {
        setReturnDetailFilterInputForDrawer(initialState.returnDetailFilterInput);
        const form = document.querySelector('form');
        if (form) form.reset();
    };
    return (
        <form
            className="return-list-filter-drawer"
            onSubmit={(e: any) => {
                e.preventDefault();
                setReturnDetailFilterInput(returnDetailFilterInputForDrawer);
                onClose();
            }}
        >
            <Box sx={{ px: 2.5, py: 2 }}>
                <Grid container spacing={0}>
                    <SCUIGeneralDrawerHeader header={t('general.detailedSearch')} onClose={onClose} />

                    <Grid size={12} sx={{ mb: 2, marginTop: '24px' }}>
                        <TextField
                            className="return-list-filter-drawer-return-id"
                            label={t('return.returnId')}
                            placeholder={t('return.returnId')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AssignmentReturnIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    ReturnId: Number(e.target.value.trim()),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-rma-code"
                            label={t('return.RMACode')}
                            placeholder={t('return.RMACode')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ConfirmationNumberIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    RMACode: Number(e.target.value.trim()),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-order-id"
                            label={t('general.orderId')}
                            placeholder={t('general.orderId')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TagIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    OrderId: Number(e.target.value.trim()),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-order-no"
                            label={t('general.orderNo')}
                            placeholder={t('general.orderNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ReceiptLongIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    OrderNumber: e.target.value.trim(),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-shipping-id"
                            label={t('general.shippingID')}
                            placeholder={t('general.shippingID')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocalShippingIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    ShippingListId: Number(e.target.value.trim()),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-shipping-no"
                            label={t('general.shippingNo')}
                            placeholder={t('general.shippingNo')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ListAltIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    ShippingListNo: e.target.value.trim(),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-customer-id"
                            label={t('general.customerId')}
                            placeholder={t('general.customerId')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    CustomerId: Number(e.target.value.trim()),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-store-code"
                            label={t('general.storeCode')}
                            placeholder={t('general.storeCode')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <StorefrontIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    StoreCode: e.target.value.trim(),
                                })
                            }
                        />
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <FormControl className="return-list-filter-drawer-status-select" fullWidth size="small">
                            <InputLabel>{t('comments.selectStatus')}</InputLabel>
                            <Select
                                fullWidth
                                label={t('comments.selectStatus')}
                                sx={selectSx}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <FilterListIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                    </InputAdornment>
                                }
                                onChange={(event: any) =>
                                    setReturnDetailFilterInputForDrawer({
                                        ...returnDetailFilterInputForDrawer,
                                        Status: event.target.value,
                                    })
                                }
                            >
                                {returnStatus &&
                                    returnStatus.map((item: any, index: number) => (
                                        <MenuItem key={index} id={item.returnStatusId} value={item.returnStatusId}>
                                            {item.statusDescription}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={12} sx={{ mb: 2 }}>
                        <TextField
                            className="return-list-filter-drawer-barcodes"
                            label={t('return.barcodes')}
                            placeholder={t('return.barcodes')}
                            fullWidth
                            size="small"
                            sx={textFieldSx}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <QrCodeScannerIcon />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(e) =>
                                setReturnDetailFilterInputForDrawer({
                                    ...returnDetailFilterInputForDrawer,
                                    Barcodes: e.target.value.trim(),
                                })
                            }
                        />
                    </Grid>

                    <Grid className="return-list-filter-drawer-date-range" size={12} sx={{ mb: 3 }}>
                        <SCUIDateRanger dateFormat="YYYY-MM-DD" setDateRange={_handlChangeDate} />
                    </Grid>

                    <Grid size={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'background.paper',
                                pt: 1,
                                pb: 0.5,
                            }}
                        >
                            <SCUIButton
                                className="return-list-filter-drawer-clear-button"
                                text={t('general.clear')}
                                onClick={clearForm}
                                buttonType="button"
                                type="primary"
                                sizeType="fixed"
                                style={{
                                    backgroundColor: '#ef4444',
                                    borderColor: '#ef4444',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                                size={'m'}
                                fullwidth
                                showStartIcon
                                startIcon={<RestartAltIcon style={{ fontSize: '16px' }} />}
                            />
                            <SCUIButton
                                className="return-list-filter-drawer-filter-button"
                                text={t('general.filter')}
                                type="primary"
                                sizeType="fixed"
                                style={{
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                                size={'m'}
                                fullwidth
                                showStartIcon
                                startIcon={<FilterListIcon style={{ fontSize: '16px' }} />}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
};
